import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiRequest, { API_URLS } from "../services/api";
import Alert from "../components/Alert";

// ─── icon primitive ───────────────────────────────────────────────────────────
const Icon = ({ d, className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
  </svg>
);

const ICONS = {
  survey:   "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  plus:     "M12 4v16m8-8H4",
  questions:"M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  responses:"M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z",
  trash:    "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  warning:  "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  x:        "M6 18L18 6M6 6l12 12",
  search:   "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  check:    "M5 13l4 4L19 7",
  hash:     "M7 20l4-16m2 16l4-16M6 9h14M4 15h14",
  active:   "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  inactive: "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z",
  chevron:  "M19 9l-7 7-7-7",
  text:     "M4 6h16M4 12h16M4 18h7",
};

const PALETTE = [
  "bg-blue-600","bg-emerald-600","bg-violet-600","bg-indigo-600",
  "bg-amber-500","bg-teal-600","bg-rose-600","bg-cyan-600",
  "bg-orange-500","bg-purple-600",
];

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-slate-50 focus:bg-white";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function Surveys() {
  const navigate = useNavigate();
  const [surveys, setSurveys]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState("");
  const [submitting, setSubmitting]       = useState(false);
  const [search, setSearch]               = useState("");

  const [showCreate, setShowCreate]       = useState(false);
  const [showDelete, setShowDelete]       = useState(false);
  const [showEdit, setShowEdit]           = useState(false);
  const [toEdit, setToEdit]             = useState(null);
  const [toDelete, setToDelete]           = useState(null);
  const [formData, setFormData]           = useState({ title: "", description: "" });

  useEffect(() => { fetchSurveys(); }, []);

  const fetchSurveys = async () => {
    try {
      const r = await apiRequest(API_URLS.admin.listSurveys);
      setSurveys(r.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch surveys");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) { setError("Survey title is required"); return; }
    setSubmitting(true);
    try {
      await apiRequest(API_URLS.admin.createSurvey, "POST", formData);
      setFormData({ title: "", description: "" });
      setShowCreate(false);
      fetchSurveys();
    } catch (err) { setError(err.message || "Failed to create survey"); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!formData.title.trim()) { setError("Survey title is required"); return; }
    setSubmitting(true);
    try {
      await apiRequest(API_URLS.admin.editSurvey(toEdit), "PUT", formData);
      setFormData({ title: "", description: "" });
      setShowEdit(false);
      setToEdit(null);
      fetchSurveys();
    } catch (err) { setError(err.message || "Failed to update survey"); }
    finally { setSubmitting(false); }
  };

  const fetchSurveyForEdit = async (surveyId) => {
    try {
      const r = await apiRequest(API_URLS.admin.getSurvey(surveyId));
      setFormData({ 
        title: r.data.title || "", 
        description: r.data.description || "" 
      });
      setToEdit(surveyId);
      setShowEdit(true);
    } catch (err) {
      setError(err.message || "Failed to fetch survey for editing");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiRequest(API_URLS.admin.deleteSurvey(toDelete), "DELETE");
      setShowDelete(false); setToDelete(null);
      fetchSurveys();
    } catch (err) { setError(err.message || "Failed to delete survey"); }
  };

  const toDeleteTitle = surveys.find(s => s.id === toDelete)?.title;

  const filtered = surveys.filter(s =>
    !search ||
    s.title?.toLowerCase().includes(search.toLowerCase()) ||
    s.description?.toLowerCase().includes(search.toLowerCase())
  );

  const activeSurveys   = surveys.filter(s => s.is_active).length;
  const inactiveSurveys = surveys.length - activeSurveys;

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 sticky top-0 z-10 shadow-sm">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Surveys</h1>
            <p className="text-xs text-slate-400 mt-0.5">Create and manage survey forms for field agents</p>
          </div>
          <button
            onClick={() => { setFormData({ title: "", description: "" }); setShowCreate(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
          >
            <Icon d={ICONS.plus} className="w-4 h-4" />
            Create Survey
          </button>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && <Alert type="error" message={error} onClose={() => setError("")} />}

        {/* ── Summary strip ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Surveys",    value: surveys.length,   color: "bg-blue-600",    icon: ICONS.survey   },
            { label: "Active",           value: activeSurveys,    color: "bg-emerald-600", icon: ICONS.active   },
            { label: "Inactive",         value: inactiveSurveys,  color: "bg-slate-400",   icon: ICONS.inactive },
            { label: "Showing",          value: filtered.length,  color: "bg-violet-600",  icon: ICONS.search   },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-9 h-9 ${c.color} rounded-lg flex items-center justify-center shrink-0`}>
                <Icon d={c.icon} className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900 leading-none">{c.value}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Surveys list ───────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

          {/* Toolbar */}
          <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-800">All Surveys</h2>
              <p className="text-xs text-slate-400 mt-0.5">{filtered.length} of {surveys.length} surveys</p>
            </div>
            <div className="relative max-w-xs w-full sm:w-auto">
              <Icon d={ICONS.search} className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search surveys…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <Icon d={ICONS.x} className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
                <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
              </div>
              <p className="text-sm text-slate-400">Loading surveys…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Icon d={ICONS.survey} className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-sm font-medium">
                {search ? `No surveys match "${search}"` : "No surveys yet"}
              </p>
              {!search && (
                <button
                  onClick={() => { setFormData({ title: "", description: "" }); setShowCreate(true); }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Create your first survey →
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((survey, i) => {
                const dotColor = PALETTE[i % PALETTE.length];
                const initial  = survey.title?.charAt(0).toUpperCase() || "S";
                return (
                  <div
                    key={survey.id}
                    className="flex items-start gap-4 px-5 py-4 hover:bg-slate-50/60 transition-colors group"
                  >
                    {/* Colour avatar */}
                    <div className={`w-10 h-10 ${dotColor} rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0 mt-0.5`}>
                      {initial}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-800 truncate">{survey.title}</h3>
                        {/* Active / inactive badge */}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${
                          survey.is_active
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-500 border-slate-200"
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${survey.is_active ? "bg-emerald-500" : "bg-slate-400"}`} />
                          {survey.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <p className="text-xs text-slate-400 mt-0.5 truncate">
                        {survey.description || "No description provided"}
                      </p>

                      <div className="flex flex-wrap items-center gap-3 mt-1.5">
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Icon d={ICONS.hash} className="w-3 h-3" />
                          ID {survey.id}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Icon d={ICONS.calendar} className="w-3 h-3" />
                          {new Date(survey.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                        {survey.questions_count != null && (
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Icon d={ICONS.questions} className="w-3 h-3" />
                            {survey.questions_count} question{survey.questions_count !== 1 ? "s" : ""}
                          </span>
                        )}
                        {survey.responses_count != null && (
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Icon d={ICONS.responses} className="w-3 h-3" />
                            {survey.responses_count} response{survey.responses_count !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0 pt-0.5">
                      <button
                        onClick={() => fetchSurveyForEdit(survey.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100 hover:bg-amber-100 transition-colors"
                      >
                        <Icon d={ICONS.text} className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={() => navigate(`/surveys/${survey.id}/questions`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors"
                      >
                        <Icon d={ICONS.questions} className="w-3.5 h-3.5" />
                        Questions
                      </button>
                      <button
                        onClick={() => navigate(`/surveys/${survey.id}/responses`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                      >
                        <Icon d={ICONS.responses} className="w-3.5 h-3.5" />
                        Responses
                      </button>
                      <button
                        onClick={() => { setToDelete(survey.id); setShowDelete(true); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-colors"
                      >
                        <Icon d={ICONS.trash} className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Create Survey Modal ───────────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon d={ICONS.survey} className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">Create New Survey</h2>
                  <p className="text-xs text-slate-400">Set up a new survey form for field agents</p>
                </div>
              </div>
              <button
                onClick={() => { setShowCreate(false); setFormData({ title: "", description: "" }); }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <Icon d={ICONS.x} className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <Field label="Survey Title" required>
                <input
                  type="text" value={formData.title}
                  onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                  className={inputCls} placeholder="e.g. Noodle Brand Survey Q3 2025"
                  autoFocus
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                  className={inputCls + " resize-none"}
                  placeholder="Brief description of what this survey covers…"
                  rows={3}
                />
                <p className="text-xs text-slate-400 mt-1">Optional — shown to enumerators before they begin.</p>
              </Field>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={handleCreate}
                disabled={submitting || !formData.title.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors"
              >
                {submitting
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Icon d={ICONS.check} className="w-4 h-4" />}
                {submitting ? "Creating…" : "Create Survey"}
              </button>
              <button
                onClick={() => { setShowCreate(false); setFormData({ title: "", description: "" }); }}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Survey Modal ───────────────────────────────────────────── */}
      {showEdit && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-amber-600 rounded-lg flex items-center justify-center">
                  <Icon d={ICONS.text} className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">Edit Survey</h2>
                  <p className="text-xs text-slate-400">Update survey details</p>
                </div>
              </div>
              <button
                onClick={() => { setShowEdit(false); setToEdit(null); setFormData({ title: "", description: "" }); }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <Icon d={ICONS.x} className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <Field label="Survey Title" required>
                <input
                  type="text" value={formData.title}
                  onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                  className={inputCls} placeholder="e.g. Noodle Brand Survey Q3 2025"
                  autoFocus
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                  className={inputCls + " resize-none"}
                  placeholder="Brief description of what this survey covers…"
                  rows={10}
                />
                <p className="text-xs text-slate-400 mt-1">shown to enumerators before they can take the survey.</p>
              </Field>
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={handleEdit}
                disabled={submitting || !formData.title.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors"
              >
                {submitting
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Icon d={ICONS.check} className="w-4 h-4" />}
                {submitting ? "Updating…" : "Update Survey"}
              </button>
              <button
                onClick={() => { setShowEdit(false); setToEdit(null); setFormData({ title: "", description: "" }); }}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ──────────────────────────────────────────── */}
      {showDelete && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex flex-col items-center text-center px-6 pt-8 pb-5">
              <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-4 ring-8 ring-red-50">
                <Icon d={ICONS.warning} className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Delete Survey</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-slate-800">"{toDeleteTitle}"</span>?
                All associated questions and responses will be permanently removed.
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={handleDelete}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                <Icon d={ICONS.trash} className="w-4 h-4" />
                Delete Survey
              </button>
              <button
                onClick={() => { setShowDelete(false); setToDelete(null); }}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}